import { Button } from '@chakra-ui/button';
import { Card } from '@chakra-ui/card';
import { useColorMode } from '@chakra-ui/color-mode';
import { Icon } from '@chakra-ui/icon';
import { AbsoluteCenter, Box, Link, Stack, Text } from '@chakra-ui/layout';
import { useRouter } from 'next/router';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Mail, RefreshCw } from 'react-feather';
import { CriticalAlertIcon } from '../../components/critical-alert-icon/critical-alert-icon';
import { colors } from '../../styles/theme/foundations/colors';
import { typography } from '../../styles/theme/foundations/typography';
import { EMAILS, STATUS_PAGE_URL } from '../link';

const ErrorFallback: React.FC<FallbackProps> = ({ error }) => {
  const router = useRouter();

  const { colorMode } = useColorMode();

  return (
    <AbsoluteCenter
      as={Card}
      p="1.5rem"
      textAlign="center"
      boxShadow="lg"
      borderRadius="0.75rem"
      width="30.25rem"
      maxWidth="100%"
    >
      <Stack direction={['column']} spacing="1.5rem" alignItems="center" width="100%">
        <CriticalAlertIcon />

        <Stack direction={['column']} spacing="0.5rem" width="100%">
          <Text {...typography.textStyles.sectionHeading}>Critical error</Text>
          <Box>
            <Text {...typography.textStyles.body} as="p">
              We&apos;re sorry – it appears something went wrong on our end.
            </Text>
            <br />
            <Text {...typography.textStyles.body} as="p">
              To mitigate the issue, please try refreshing the app. If this issue persists, contact us to inform us of
              the issue. You can view our{' '}
              <Link
                {...typography.textStyles.bodyMedium}
                color={colors.text.hyperlink[colorMode]}
                href={STATUS_PAGE_URL}
                isExternal
                rel="noopener noreferrer"
                target="_blank"
              >
                system status
              </Link>{' '}
              for known issues being investigated.
            </Text>
            <br />
            <Text {...typography.textStyles.caption} as="p">
              Returned error: {(error as Error)?.message}
            </Text>
          </Box>
        </Stack>

        <Stack direction={['column', 'row']} justifyContent="center" spacing={['0.375rem']} width="100%">
          <Button variant="primarySolid" leftIcon={<Icon as={RefreshCw} />} onClick={router.reload}>
            Refresh app
          </Button>
          <Button
            variant="primaryOutline"
            leftIcon={<Icon as={Mail} />}
            as={Link}
            href={`mailto:${EMAILS.HELP_NY}`}
            _hover={{ textDecoration: 'none' }}
          >
            Contact us
          </Button>
        </Stack>
      </Stack>
    </AbsoluteCenter>
  );
};

export const ClientErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { asPath } = useRouter();

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      // we want the error boundary to reset when the route changes
      resetKeys={[asPath]}
    >
      {children}
    </ErrorBoundary>
  );
};
